// src/lib/stableChanges.js — T34: a change exists when a value SETTLES, not when someone saves.
//
// OWNER RULING 2026-09-02, verbatim: "A history change exists only when a field reaches a new
// value and remains unchanged for 15 minutes. The displayed change compares that stable value
// against the previous stable value." Per field, SLIDING timer (restarts on every edit to that
// field), previous-stable → new-stable.
//
// The problem it solves, in one real shape: an agent typing a price produces
//     14:02 price 250,000 → 25,000
//     14:02 price 25,000 → 250,000
//     14:03 price 250,000 → 240,000
// Three lines say the price moved three times. It moved once. The history is what you read to
// answer "has this owner been dropping the price?", so editing activity drowning out market
// state is not untidiness — it is the log failing at its only job.
//
// 🔴 THE HALF THAT KEEPS IT HONEST, and the reason a fixed bucket was rejected (owner):
// "if 250k -> 245k genuinely survives 20 minutes and then becomes 240k, that should be two
// changes, not one. The 245k was a real published state for 20 minutes. Otherwise you start
// rewriting actual market history in the name of cleanliness."
// So: collapse editing ACTIVITY, never collapse market STATE. Every rule below is one of those
// two sentences; if a future change cannot be argued from one of them, it is a different feature.
//
// ⚠️ WHY SLIDING AND NOT A FIXED 15-MINUTE BUCKET (owner): "human beings will inevitably manage
// to make a typo at minute 14:59 and defeat the entire mechanism, because naturally they would."
// A fixed window has a seam, and a seam in a rule about typos is found by typos.
//
// ⚠️ THIS IS A PRESENTATION RULE, NOT A STORAGE RULE. Every save still writes its event; nothing
// is delayed, dropped or rewritten at the write path. This module only decides which lines get
// DRAWN, which is why the threshold can change later with no migration and the whole history
// simply redraws. Do not "optimise" it by filtering at capture — that destroys the raw record the
// rule is derived from, and the append-only claim model is load-bearing elsewhere.
//
// 📌 It is deliberately the SAME rule T36 needs. The owner's contribution ruling says credit
// attaches to "the resolved fact transition, not every edit — if I change district A->B->A->B I
// cannot manufacture four contributions". That is this function. When T36 is built it must
// CONSUME this, not restate it; two definitions of "what actually changed" would drift, which is
// this codebase's most expensive recurring failure.

export const STABILITY_MS = 15 * 60 * 1000

// Loose, because from/to arrive as whatever the writer stored — 240000 and "240000" are the same
// state, and treating them as different would report a change nobody made.
const same = (a, b) => (a == null && b == null) || String(a) === String(b)

// One edit, flattened out of an event. An event can carry SEVERAL fields, so they are fanned out
// here and re-assembled at the end: a scrape that moves price and area at once may legitimately
// have one of them settle and the other not.
function editsOf(events) {
  const out = []
  for (const e of events || []) {
    if (e?.verb !== 'listing.changed') continue
    const ts = Date.parse(e.occurred_at)
    if (!Number.isFinite(ts)) continue          // an unparseable stamp cannot be reasoned about
    for (const [field, x] of Object.entries(e.data || {})) {
      if (!x || typeof x !== 'object' || !('from' in x)) continue
      out.push({ id: e.id, ts, field, key: `${e.subject_id ?? ''}|${field}`, from: x.from, to: x.to })
    }
  }
  return out.sort((a, b) => a.ts - b.ts || String(a.id).localeCompare(String(b.id)))
}

/**
 * Decide which edits are settled state and which are typing.
 *
 * Returns one entry per SURVIVING transition, oldest first:
 *   { id, ts, field, key, from, to, pending }
 * where `from` is the previous STABLE value (not the value the edit itself replaced) and
 * `pending` marks the newest edit to a field when it has not yet held for the window.
 *
 * @param events  raw timeline events (any order; only `listing.changed` is considered)
 * @param nowMs   the CORRECTED instant — pass clock.now(). Never Date.now(): a device clock
 *                running 20 minutes fast marks everything stable, which silently turns the whole
 *                feature off in exactly the way nobody would notice.
 */
export function stableChanges(events, nowMs, windowMs = STABILITY_MS) {
  const byKey = new Map()
  for (const ed of editsOf(events)) {
    if (!byKey.has(ed.key)) byKey.set(ed.key, [])
    byKey.get(ed.key).push(ed)
  }

  const kept = []
  for (const series of byKey.values()) {
    // The value in place BEFORE the first edit we can see. It is treated as stable: it is what
    // the world already believed, and whatever happened before our window is not this log's
    // business. Getting this wrong makes the first real change render "— → 240,000".
    let prevStable = series[0].from

    series.forEach((ed, i) => {
      const next = series[i + 1]
      // A value lasts until the NEXT edit to the same field. Sliding by construction: the test is
      // always against the following edit, so a typo at minute 14:59 restarts the clock.
      const heldMs = (next ? next.ts : nowMs) - ed.ts
      const settled = heldMs >= windowMs

      if (!settled && next) return                    // typing. It never was a state; drop it.

      // The transition as the world experienced it: previous settled value → this one. The values
      // in between were never true for long enough to be worth a line.
      if (!same(prevStable, ed.to)) kept.push({ ...ed, from: prevStable, pending: !settled })

      // ⚠️ Only a SETTLED value becomes the new baseline. A pending value must not, or the next
      // edit would be measured against something that may yet be revoked — and A→B(pending)→A
      // would then report a change back to A that never happened.
      if (settled) prevStable = ed.to
    })
  }
  return kept.sort((a, b) => a.ts - b.ts || String(a.id).localeCompare(String(b.id)))
}

/**
 * Rewrite a timeline event list so `listing.changed` rows show settled transitions.
 *
 * ⚠️ THE PENDING ROW IS NOT OPTIONAL and is the single most likely way to ship this wrong. The
 * newest value has BY DEFINITION not been stable for the window yet, so a strict reading would
 * hide the change an agent just made — they look at the history to confirm their save landed, see
 * nothing, and reasonably conclude the save failed. It is drawn, marked `pending`.
 *
 * ⚠️ A field edited every 10 minutes forever never settles, and would log nothing while genuinely
 * moving. The pending row is also what saves that case. Callers must therefore never render
 * "no changes recorded" on the strength of an empty settled list alone — "nothing changed" and
 * "not settled yet" are different sentences.
 */
export function stabilizeEvents(events, nowMs, windowMs = STABILITY_MS) {
  const evs = events || []
  const keptByEvent = new Map()
  for (const c of stableChanges(evs, nowMs, windowMs)) {
    if (!keptByEvent.has(c.id)) keptByEvent.set(c.id, {})
    keptByEvent.get(c.id)[c.field] = { from: c.from, to: c.to, pending: c.pending }
  }
  const out = []
  for (const e of evs) {
    if (e?.verb !== 'listing.changed') { out.push(e); continue }
    const fields = keptByEvent.get(e.id)
    if (!fields) continue                       // every field on this event was typing
    // Non-diff keys (source, actor, …) are preserved: the row still has to say WHERE it came
    // from. Only the from/to pairs are replaced by their settled form.
    const rest = Object.fromEntries(Object.entries(e.data || {})
      .filter(([, x]) => !(x && typeof x === 'object' && 'from' in x)))
    out.push({ ...e, data: { ...rest, ...fields } })
  }
  return out
}
